<template>
    <!-- Variable-length list of a single inner field type (e.g. Room Condition's
         "times to send report each day"). Proves the registry contract holds for
         a non-trivial, add/remove control, not just flat label+input rows. -->
    <div :class="compact ? 'py-2' : 'py-3'">
        <!-- Compact (a bare section, e.g. Report Details): the designs' bold
             label, with the description behind an info tip. -->
        <div    v-if="compact"
                class="mb-1 flex items-center gap-2">
            <label class="m-0 block text-left text-sm font-semibold text-gray-900">{{ field.label }}</label>
            <info-tip   v-if="field.description"
                        :text="field.description"
            ></info-tip>
        </div>
        <label  v-else
                class="mb-2 block text-left text-sm font-medium text-gray-700">{{ field.label }}</label>

        <p  v-if="field.description && ! compact"
            class="m-0 mb-2 text-left text-sm text-gray-500">
            {{ field.description }}
        </p>

        <div    v-if="rows.length === 0"
                class="mb-2 rounded-md bg-gray-50 px-3 py-2.5 text-left text-sm text-gray-500">
            {{ field.emptyText || 'None set.' }}
        </div>

        <!-- One row per value: the inner control, then a remove button. The rows
             are not individually labelled -- their position is the label, and a
             per-row heading pushed the control out of line with the button
             beside it. -->
        <div    v-for="(row, index) in rows"
                :key="`${field.key}_${index}`"
                class="mb-2 flex items-center gap-2"
        >
            <div class="min-w-0 flex-1">
                <component  :is="itemComponent"
                            :model-value="row"
                            :field="itemField(index)"
                            :timezone="timezone"
                            @update:modelValue="updateRow(index, $event)"
                ></component>
            </div>

            <!-- Icon-only: a "Remove" word button next to a compact input made
                 the row read as two competing controls. -->
            <button type="button"
                    class="flex h-[35px] w-[35px] shrink-0 items-center justify-center rounded-md border border-gray-300 text-gray-400 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    @click="removeRow(index)"
            >
                <span class="sr-only">Remove {{ field.itemLabel || 'item' }} {{ index + 1 }}</span>
                <tabler-icon name="x" size-class="h-4 w-4"></tabler-icon>
            </button>
        </div>

        <!-- Indented by the remove button's width plus the row gap, so its right
             edge lines up with the inputs above rather than overhanging them. -->
        <button v-if="canAdd"
                type="button"
                class="mt-1 flex h-[35px] w-[calc(100%-43px)] items-center justify-center gap-1.5 rounded-md border border-dashed border-gray-300 text-sm font-medium text-gray-600 transition-colors duration-150 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
                @click="addRow"
        >
            <tabler-icon name="plus" size-class="h-4 w-4"></tabler-icon>
            {{ field.addText || 'Add' }}
        </button>
    </div>
</template>

<script>
import InfoTip from "@/components/elements/InfoTip.vue"
import TablerIcon from "@/components/elements/TablerIcon.vue"
import {FIELD_TYPES} from "../fieldTypes"

export default {
    name: "ReportRepeaterField",
    components: {TablerIcon, InfoTip},
    // Set by a bare ReportSettingsSection.
    inject: {
        compact: {from: 'reportFieldRowCompact', default: false},
    },
    emits: ['update:modelValue'],
    props: {
        modelValue: {
            type: Array,
            default: () => ([]),
        },
        // Requires `field.itemType` (a registered field type) and optionally
        // `field.itemLabel`, `field.max`, `field.default`.
        field: {
            type: Object,
            required: true,
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
    },
    computed: {
        rows() {
            return Array.isArray(this.modelValue) ? this.modelValue : []
        },
        itemComponent() {
            return FIELD_TYPES[this.field.itemType]
        },
        canAdd() {
            return this.field.max === undefined || this.rows.length < this.field.max
        },
    },
    methods: {
        /**
         * The inner field always renders unlabelled. `itemLabel` still matters
         * -- it names the remove button for screen readers -- but rendering it
         * above each control stacked a heading inside every row and knocked the
         * control out of line with the button beside it.
         */
        itemField(index) {
            return {
                ...(this.field.itemField || {}),
                key: `${this.field.key}_${index}`,
                label: '',
            }
        },
        emitRows(rows) {
            this.$emit('update:modelValue', rows)
        },
        updateRow(index, value) {
            const next = [...this.rows]
            next[index] = value
            this.emitRows(next)
        },
        addRow() {
            this.emitRows([...this.rows, this.field.default ?? null])
        },
        removeRow(index) {
            this.emitRows(this.rows.filter((row, i) => i !== index))
        },
    },
}
</script>
